import type { UserProfile } from '$shared/types/api';
import {
  ACCOUNT_TYPE,
  canAccessClara,
  isGeneralPublic,
  isHealthcareProfessional,
  isIndustryProfessional,
} from '$shared/lib/account-type';

const CLARA_ENTRY_KEY = 'claraEntry';
const PENDING_CAPTURE_KEY = 'claraPendingCaptureId';

export function markClaraEntry(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(CLARA_ENTRY_KEY, 'true');
  }
}

export function clearClaraEntry(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(CLARA_ENTRY_KEY);
  }
}

function isClaraEntry(): boolean {
  return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(CLARA_ENTRY_KEY) === 'true';
}

/**
 * Remember the partner capture the reader arrived with.
 *
 * Signing in honours `?redirect=`, but signing up does not — a new account is routed by
 * onboarding state, which would otherwise drop the article they came to reflect on. Parking the
 * id here lets the capture survive the whole account-creation detour.
 */
export function markPendingCapture(captureId: string): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(PENDING_CAPTURE_KEY, captureId);
  }
}

export function peekPendingCapture(): string | null {
  return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(PENDING_CAPTURE_KEY) : null;
}

export function clearPendingCapture(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(PENDING_CAPTURE_KEY);
  }
}

export function claraCaptureHref(captureId: string): string {
  return `/clara/capture/${encodeURIComponent(captureId)}`;
}

export function isOnboardingPath(pathname: string): boolean {
  return pathname === '/onboarding' || pathname.startsWith('/onboarding/');
}

const PUBLIC_ROUTES = [
  '/login',
  '/auth/callback',
  '/forgot-password',
  '/reset-password',
  '/signup',
  '/signup/verify',
  '/clara',
  '/clara/signup',
  '/clara/verify',
];

/** Routes anyone can open without an account; the guard skips them and no shell wraps them. */
export function isPublicPath(pathname: string): boolean {
  return (
    PUBLIC_ROUTES.includes(pathname) ||
    isClaraPublicPath(pathname) ||
    pathname.startsWith('/sso/') ||
    // Readers arrive on all three straight from a partner article or an emailed link, before
    // they have a STAT account. Each page decides what to offer them; the guard must not bounce
    // them to signup first.
    pathname.startsWith('/clara/capture/') ||
    pathname.startsWith('/clara/reflection/') ||
    pathname.startsWith('/clara/claim/')
  );
}

export function isClaraPublicPath(pathname: string): boolean {
  return (
    pathname === '/signup' ||
    pathname === '/signup/verify' ||
    pathname === '/clara' ||
    pathname === '/clara/signup' ||
    pathname === '/clara/verify'
  );
}

function needsGeneralStripeSetup(profile: UserProfile): boolean {
  return isGeneralPublic(profile) && !profile.stripeAccount?.id;
}

function needsIndustryOrganisation(profile: UserProfile): boolean {
  return isIndustryProfessional(profile) && profile.missingSetupItems?.includes('OrganisationMissing') === true;
}

export function isIndustryJoinPendingPath(pathname: string): boolean {
  return pathname === '/onboarding/industry/join-pending';
}

/**
 * Next route after Cognito auth — mirrors mobile auth listener + CLARA.md.
 */
export function resolvePostAuthDestination(profile: UserProfile | null): string {
  if (!profile?.accountType) {
    return '/onboarding/account-type';
  }

  if (profile.needsSetup && profile.missingSetupItems?.length) {
    if (profile.missingSetupItems.includes('OrganisationMissing') && isIndustryProfessional(profile)) {
      return '/onboarding/industry/organisation';
    }
    if (profile.missingSetupItems.includes('AHPRARecord') && isHealthcareProfessional(profile)) {
      return '/onboarding/healthcare';
    }
    if (profile.missingSetupItems.some((item) => ['FirstName', 'LastName', 'DateOfBirth'].includes(item))) {
      return onboardingPathForAccountType(profile.accountType);
    }
  }

  if (isHealthcareProfessional(profile)) {
    const ahpraMissing = profile.missingSetupItems?.includes('AHPRARecord');
    const hasAhpra = !!profile.healthCareRecord?.ahpraRecord?.registrationNumber;
    if (ahpraMissing || !hasAhpra) {
      return '/onboarding/healthcare';
    }
    if (isClaraEntry() || canAccessClara(profile)) {
      clearClaraEntry();

      // Back to the article they pressed "Reflect on this" on, not the generic hub.
      const pendingCapture = peekPendingCapture();
      if (pendingCapture) {
        return claraCaptureHref(pendingCapture);
      }

      return '/dashboard/clara';
    }
  }

  if (needsIndustryOrganisation(profile)) {
    return '/onboarding/industry/organisation';
  }

  if (needsGeneralStripeSetup(profile)) {
    return '/onboarding/general/banking';
  }

  return '/';
}

function onboardingPathForAccountType(accountType: string): string {
  switch (accountType.toLowerCase()) {
    case ACCOUNT_TYPE.healthcareProfessional:
      return '/onboarding/healthcare';
    case ACCOUNT_TYPE.industryProfessional:
      return '/onboarding/industry';
    case ACCOUNT_TYPE.generalPublic:
      return '/onboarding/general';
    default:
      return '/onboarding/account-type';
  }
}
